import React from 'react';
import PropTypes from 'prop-types';
import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';
import Image from 'src/components/Image';

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const EsVersion = ({ id, availableLanguages }) => {
  return (
    <>
      <h4 id={id || 'declaración-de-privacidad'}>
        Todos los destinatarios <br />
        Declaración de privacidad
      </h4>

      <p>Idiomas disponibles:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Fecha de entrada en vigor: 22 de febrero de 2024</p>
      <p>
        ¡Gracias por usar Plaid! Esperamos que se tome un tiempo para leer
        detenidamente esta Declaración de privacidad.
      </p>
      <p>
        La presente Declaración de privacidad explica cómo Plaid Inc. y sus
        subsidiarias, incluidas Plaid Financial Ltd., Plaid Consumer Reporting
        Agency, Inc. y Plaid, B.V., (conjuntamente, “Plaid”, “nosotros”,
        “nuestro” y “nos”) recabamos, usamos y compartimos información sobre
        usted (es decir, el desarrollador, cliente, visitante del sitio web)
        cuando visita o utiliza los sitios web de Plaid o incorpora nuestros
        productos y servicios en su aplicación de software, o bien, interactúa
        de otro modo con nosotros (por ejemplo, cuando hace una consulta de
        servicio al cliente o asiste a un evento o seminario web de Plaid). Esta
        Declaración de privacidad no se aplica a la información que recabamos
        sobre los usuarios finales cuando conectan sus cuentas financieras a una
        aplicación a través de Plaid, ni a los datos que se usan para crear un
        informe de consumidor de Plaid CRA. Si busca información sobre cómo
        gestionamos los datos recopilados en esas circunstancias, consulte
        nuestra{' '}
        <Link href='#política-de-privacidad-aplicable-al-usuario-final'>
          <a>Política de privacidad aplicable al usuario final</a>
        </Link>{' '}
        o la{' '}
        <Link href='/plaid-check-consumer-report/privacy-policy/'>
          <a>
            Política de privacidad de la agencia de información al consumidor
            Plaid
          </a>
        </Link>
        .
      </p>
      <p>
        <em>Ir a la sección:</em>
      </p>
      <p>
        <Link href='#nuestras-prácticas-relativas-a-datos-privacidad'>
          <a data-smooth-scroll data-offset='80'>
            Nuestras prácticas relativas a datos
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#información-que-recabamos-y-categorías-de-fuentes-privacidad'>
              <a>Información que recabamos y categorías de fuentes</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#cómo-utilizamos-su-información-privacidad'>
              <a>Cómo utilizamos su información</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#nuestras-bases-legitimadoras-para-el-tratamiento-privacidad'>
              <a>
                Nuestras bases legitimadoras para el tratamiento en el EEE y el
                Reino Unido
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#cómo-compartimos-su-información-privacidad'>
              <a>Cómo compartimos su información</a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#otras-cuestiones-privacidad'>
          <a data-smooth-scroll data-offset='80'>
            Otras cuestiones
          </a>
        </Link>
      </p>

      <ul data-smooth-scroll data-offset='80'>
        <li>
          <p>
            <Link href='#chatbots-publicidad-análisis-y-otros-servicios-prestados-por-terceros'>
              <a>
                Chatbots, publicidad, análisis y otros servicios prestados por
                terceros
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#transferencias-internacionales-de-datos'>
              <a>Transferencias internacionales de datos</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#conservación-de-datos'>
              <a>Conservación de datos</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#sus-opciones-privacidad'>
              <a>Sus opciones</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#sus-derechos-en-materia-de-protección-de-datos-privacidad'>
              <a>Sus derechos en materia de protección de datos</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#cambios-en-esta-declaración'>
              <a>Cambios en esta declaración</a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#cómo-ponerse-en-contacto-con-plaid-privacidad'>
              <a>Cómo ponerse en contacto con Plaid</a>
            </Link>
          </p>
        </li>
      </ul>
      <br />
      <br />

      <h4 id='nuestras-prácticas-relativas-a-datos-privacidad'>
        Nuestras prácticas relativas a datos
      </h4>
      <h5 id='información-que-recabamos-y-categorías-de-fuentes-privacidad'>
        Información que recabamos y categorías de fuentes
      </h5>
      <p>
        <strong>Información que nos proporciona</strong>. Recabamos la
        información que usted facilita directamente a Plaid. Por ejemplo, usted
        proporciona información personal cuando crea una cuenta de
        desarrollador, completa nuestro formulario de “contacto”, se registra
        para recibir nuestros correos electrónicos, solicita asistencia al
        cliente, se registra a efectos de facturación o se comunica por otro
        medio con nosotros. Los tipos de información que recabamos incluyen:
      </p>
      <ul>
        <li>
          <p>
            identificadores (como nombre completo, dirección de correo
            electrónico, dirección postal, número de teléfono y fecha de
            nacimiento);
          </p>
        </li>
        <li>
          <p>
            información confidencial (como número del permiso de conducción,
            número de identificación fiscal, información de una cuenta
            financiera y números del seguro social, según sea necesario para
            prestar servicios);
          </p>{' '}
        </li>
        <li>
          <p>
            información profesional (como el nombre de la empresa y su cargo); y
          </p>
        </li>
        <li>
          <p>
            cualquier otra información personal que decida proporcionar
            (incluidos, por ejemplo, mensajes de solicitud de asistencia al
            desarrollador).
          </p>
        </li>
      </ul>
      <p>
        También podemos recabar cierta documentación que contiene su fotografía
        o podemos requerirle que verifique su identidad proporcionando una
        fotografía o video de usted. Los datos recabados en este contexto pueden
        considerarse datos biométricos en algunos estados o países, como se
        indica a continuación. Consulte el aviso especial para residentes de
        Illinois y Texas, a continuación.
      </p>
      <p>
        <u>
          Aviso especial de datos biométricos para residentes de Illinois y
          Texas
        </u>
      </p>
      <p>
        Para los residentes de Illinois o Texas, si le solicitamos que nos
        facilite algún documento que contenga su fotografía o si necesita
        verificar su identidad proporcionando una fotografía o un video suyo,
        los datos derivados de su rostro que nosotros y nuestros proveedores de
        servicios arecopilemos y procesemos en nuestro nombre pueden
        considerarse datos biométricos en algunas jurisdicciones. Sus datos se
        almacenarán durante un máximo de tres años, a menos que la ley exija lo
        contrario.
      </p>
      <p>
        <strong>
          Información que recibimos cuando prueba nuestra tecnología
        </strong>
        . A veces, las personas que crean aplicaciones utilizando nuestros
        servicios utilizan su propia información de inicio de sesión para su
        cuenta bancaria u otra cuenta financiera para probar y evaluar el
        aspecto y el funcionamiento de nuestra tecnología en su aplicación. Si
        usted prueba nuestra tecnología de esta manera, debe leer nuestra{' '}
        <Link href='#política-de-privacidad-aplicable-al-usuario-final'>
          <a>Política de privacidad aplicable al usuario final</a>
        </Link>{' '}
        para obtener detalles sobre cómo recabamos y manejamos esa información.
      </p>
      <p>
        <strong>Información que recabamos cuando usa nuestros servicios</strong>
        . Cuando usa nuestros servicios, recabamos la siguiente información
        sobre usted:
      </p>
      <ul>
        <li>
          <p>
            <strong>Información de registro</strong>: Recabamos archivos de
            registro cuando usa nuestros servicios. Esto incluye identificadores
            e información sobre la actividad de las redes electrónicas, como el
            tipo de navegador que usa, las veces que accede a nuestros
            servicios, las páginas que visita y su dirección IP.
          </p>
        </li>
        <li>
          <p>
            <strong>Ubicación aproximada</strong>: Determinamos la ubicación
            aproximada del dispositivo que utiliza para acceder a nuestros
            servicios desde su dirección IP o configurar la zona horaria en su
            dispositivo.
          </p>
        </li>
        <li>
          <p>
            <strong>
              Información recabada mediante cookies y otras tecnologías de
              seguimiento
            </strong>
            : Usamos varias tecnologías como cookies y balizas web en nuestros
            servicios para recabar información personal. Las cookies son
            pequeños archivos de datos almacenados por su navegador web, en su
            disco duro o en la memoria del dispositivo que nos ayudan a mejorar
            nuestros servicios y su experiencia, determinar el uso de nuestros
            servicios y controlar y detectar potenciales conductas
            perjudiciales. Las balizas web son imágenes electrónicas que se
            utilizan en nuestros servicios o correos electrónicos, y ayudan a
            instalar cookies. Las balizas web contabilizan cuántas veces se
            visita una página web y nos ayudan a comprender cómo se utiliza
            nuestro Servicio y a mejorarlo.
          </p>
          <ul>
            <li>
              <p>
                Tenga en cuenta que podemos utilizar cookies de terceros para
                nuestros fines publicitarios, según lo permita la ley y según se
                describe en más detalle en nuestra{' '}
                <Link href='#política-de-cookies'>
                  <a>Política de cookies</a>
                </Link>
                . Para cambiar su configuración y preferencias de cookies,
                incluidas las de cookies de segmentación y publicidad, haga clic
                en el enlace “Cookie Preferences” (Preferencias de cookies) en
                el pie de página.
              </p>
            </li>
          </ul>
        </li>
      </ul>
      <p>
        <strong>Información que recopilamos de otras fuentes</strong>. También
        recabamos información sobre usted de otras fuentes. Por ejemplo, podemos
        recabar información de otros miembros de su empresa y de proveedores que
        nos ayudan a identificar nuevos clientes comerciales, incluido su
        nombre, dirección de correo electrónico y URL del perfil de las redes
        sociales.
      </p>

      <h5 id='cómo-utilizamos-su-información-privacidad'>
        Cómo utilizamos su información
      </h5>
      <p>
        Usamos la información que recabamos con los siguientes fines
        empresariales y comerciales:
      </p>
      <ul>
        <li>
          <p>para hacer funcionar, mejorar y desarrollar nuestros servicios;</p>
        </li>
        <li>
          <p>para comprobar su identidad;</p>
        </li>
        <li>
          <p>
            para facturar a los desarrolladores por nuestros servicios y enviar
            pagos;
          </p>
        </li>
        <li>
          <p>
            para cumplir la ley, por ejemplo, a efectos de la declaración de
            impuestos;
          </p>
        </li>
        <li>
          <p>
            para enviarle avisos técnicos, actualizaciones, alertas de seguridad
            y mensajes sobre su cuenta;
          </p>
        </li>
        <li>
          <p>
            para responder a sus comentarios, preguntas y solicitudes al
            servicio de atención al cliente;
          </p>
        </li>
        <li>
          <p>para ayudar a personalizar su experiencia con los servicios;</p>
        </li>
        <li>
          <p>
            para communicarle productos, servicios, ofertas y actos organizados
            o patrocinados por Plaid;
          </p>
        </li>
        <li>
          <p>
            para monitorizar y analizar tendencias, usos y actividades
            relacionadas con nuestros servicios;
          </p>
        </li>
        <li>
          <p>
            para detectar y evitar fraudes, actividades maliciosas y otras
            actividades ilegales;
          </p>
        </li>
        <li>
          <p>
            para proteger los derechos, la privacidad, la seguridad o la
            propiedad de Plaid y otros; y
          </p>
        </li>
        <li>
          <p>
            para cualquier otra finalidad que le notifiquemos antes o en el
            momento en que recabamos la información.
          </p>
        </li>
      </ul>

      <h5 id='nuestras-bases-legitimadoras-para-el-tratamiento-privacidad'>
        Nuestras bases legitimadoras para el tratamiento en el EEE y el Reino
        Unido
      </h5>
      <p>
        Plaid solo trata su información personal cuando tenemos una base
        legitimadora válida para hacerlo. Nuestra base legitimadora para el
        tratamiento de la información que recabamos dependerá de la información
        en cuestión y del contexto en que la tratamos. Por lo general, solo
        recabamos y tratamos información cuando:
      </p>
      <ul>
        <li>
          <p>
            debemos cumplir nuestras responsabilidades y obligaciones en
            cualquier contrato o acuerdo celebrado con usted;
          </p>
        </li>
        <li>
          <p>
            debemos cumplir nuestras obligaciones legales en virtud de la
            legislación aplicable;
          </p>
        </li>
        <li>
          <p>
            el tratamiento es necesario para nuestros intereses legítimos y no
            prevalecen sus intereses de protección de datos o derechos y
            libertades fundamentales (por ejemplo, para prestar o actualizar
            nuestros Servicios); y
          </p>
        </li>
        <li>
          <p>nos ha dado su consentimiento para hacerlo.</p>
        </li>
      </ul>

      <h5 id='cómo-compartimos-su-información-privacidad'>
        Cómo compartimos su información
      </h5>
      <p>Compartimos información sobre usted de la siguiente manera:</p>
      <ul>
        <li>
          <p>
            Con otras empresas o personas, como prestadores de servicios,
            contratistas o socios comerciales, que nos prestan servicios y
            respaldan nuestro negocio. También compartimos información con
            agencias de cobro para los pagos atrasados de nuestros
            desarrolladores.
          </p>
        </li>
        <li>
          <p>
            Si creemos de buena fe que la divulgación es adecuada para cumplir
            la legislación, la normativa o el proceso legal aplicables (como una
            orden o citación judicial). Esto incluye divulgar información a
            pedido de las fuerzas de orden público u otras autoridades
            gubernamentales.
          </p>
        </li>
        <li>
          <p>
            Si consideramos que sus acciones incumplen nuestros acuerdos o
            políticas, o para proteger los derechos, la privacidad, la seguridad
            o la propiedad de Plaid u otros.
          </p>
        </li>
        <li>
          <p>
            En el caso de un cambio de titularidad o control de la totalidad o
            parte de nuestra empresa (como una fusión, adquisición,
            reestructuración o quiebra).
          </p>
        </li>
        <li>
          <p>
            Entre Plaid y nuestras matrices, filiales, subsidiarias y otras
            empresas presentes y futuras que se encuentren bajo un control o
            titularidad común.
          </p>
        </li>
        <li>
          <p>Con su consentimiento o según sus indicaciones.</p>
        </li>
      </ul>
      <p>
        También recabamos, usamos y compartimos información agregada o anónima
        de modo que no se pueda utilizar para identificarlo o para cualquier
        finalidad que permita la ley aplicable.
      </p>
      <p>
        Podemos compartir Datos Personales con terceros o permitirles recopilar
        Datos Personales de nuestros sitios o Servicios, como se ha descrito
        anteriormente.{' '}
      </p>
      <p>
        Esta tabla explica cómo hemos recabado y compartido información personal
        en los últimos 12 meses:
      </p>
      <br />

      <p>CATEGORÍAS DE INFORMACIÓN PERSONAL</p>
      <Image
        src='https://images.ctfassets.net/ss5kfr270og3/5MlWjpfLeA9KsBKnagxEjd/7898fc2f89665e8f7cd98ec8d8d41bf0/personal-information-table-es_2x.png?fm=webp&q=70'
        alt='Información Personal Tabla'
        isLazy={false}
      />

      <h4 id='otras-cuestiones-privacidad'>Otras cuestiones</h4>
      <h5 id='chatbots-publicidad-análisis-y-otros-servicios-prestados-por-terceros'>
        Chatbots, publicidad, análisis y otros servicios prestados por terceros
      </h5>
      <p>
        Permitimos que terceros, incluido Google Analytics, presten servicios de
        análisis y publicidad en nuestros sitios web. Estos terceros utilizan
        cookies, balizas web y otras tecnologías para recabar información sobre
        usted. Plaid utiliza chatbots desarrollados por proveedores de servicios
        externos, tenga en cuenta que si proporciona información personal a
        nuestro chatbot, su información puede compartirse con el proveedor de
        servicios externo con el fin de potenciar el chatbot.
      </p>
      <p>
        Puede encontrar más información sobre cómo utilizamos las cookies y sus
        opciones relacionadas en nuestra{' '}
        <Link href='#política-de-cookies'>
          <a>Política de cookies</a>
        </Link>
        .
      </p>

      <h5 id='transferencias-internacionales-de-datos'>
        Transferencias internacionales de datos
      </h5>
      <p>
        Podemos transferir la información que recabamos sobre usted a otros
        países que no son su país de residencia para su tratamiento y
        almacenamiento. Si transferimos información a un país o territorio que
        no se considera que tenga leyes de privacidad y protección de datos
        equivalentes a las de su país, transferiremos dichos datos de acuerdo
        con las leyes de protección de datos aplicables. En particular, podemos
        utilizar cláusulas contractuales tipo aprobadas por la Comisión Europea
        o por el Secretario de Estado del Reino Unido (según corresponda), si es
        necesario en combinación con medidas de salvaguardia adicionales. Puede
        solicitar una copia de estas cláusulas contractuales tipo comunicándose
        con nosotros como se establece a continuación.
      </p>
      <h5 id='conservación-de-datos'>Conservación de datos</h5>
      <p>
        Conservamos sus datos personales solo durante el tiempo necesario para
        lograr los fines para los que se recopilaron, a menos que la legislación
        aplicable exija un período de conservación más extenso.
      </p>

      <h5 id='sus-opciones-privacidad'>Sus opciones</h5>
      <ul>
        <li>
          <p>
            <strong>Información de la cuenta del desarrollador</strong>. Puede
            actualizar la información que nos proporciona como parte de su
            cuenta de desarrollador en línea iniciando sesión en su cuenta o
            poniéndose en contacto con nosotros.
          </p>
        </li>
        <li>
          <p>
            <strong>Cookies</strong>. Puede encontrar más información sobre cómo
            utilizamos las cookies y sus opciones relacionadas en nuestra{' '}
            <Link href='#política-de-cookies'>
              <a>Política de cookies</a>
            </Link>
            .
          </p>
        </li>
        <li>
          <p>
            <strong>Comunicaciones comerciales</strong>. Puede optar por no
            recibir correos electrónicos comerciales de Plaid siguiendo las
            instrucciones en esos correos electrónicos. Si elige no recibirlos,
            igualmente podemos enviarle otros tipos de correos electrónicos,
            como soporte, servicio y otros correos electrónicos sobre su cuenta
            o nuestra relación comercial en curso.
          </p>
        </li>
      </ul>

      <h5 id='sus-derechos-en-materia-de-protección-de-datos-privacidad'>
        Sus derechos en materia de protección de datos
      </h5>
      <p>
        Puede gozar de derechos relacionados con su información personal,
        incluido el derecho a saber cómo recabamos, usamos o compartimos su
        información. Asimismo, puede tener derecho a solicitar acceso a su
        información personal, así como a intentar actualizar, eliminar o
        corregir dicha información. Cumpliremos con su solicitud de ejercer
        estos derechos independientemente de su ubicación. No se le discriminará
        por ejercer estos derechos.
      </p>
      <p>
        En determinadas jurisdicciones, incluidos el EEE y el Reino Unido, es
        posible que goce de derechos adicionales. Estos derechos incluyen el
        derecho a: oponerse o solicitar que restrinjamos el tratamiento de sus
        datos personales; revocar su consentimiento en cualquier momento cuando
        nuestro tratamiento se base en su consentimiento; y presentar una
        reclamación ante su autoridad supervisora local (si está en el EEE,
        consulte{' '}
        <Link href='https://edpb.europa.eu/about-edpb/board/members_es'>
          <a>https://edpb.europa.eu/about-edpb/board/members_es</a>
        </Link>{' '}
        y si está en el Reino Unido, consulte{' '}
        <Link href='https://ico.org.uk/'>
          <a>www.ico.org.uk</a>
        </Link>{' '}
        para obtener los datos de contacto).
      </p>
      <p>
        Tanto para Plaid Financial Ltd. como para Plaid, B.V. hemos nombrado a
        un delegado de protección de datos (data protection officer, DPO) que
        también es responsable de supervisar las preguntas en relación con esta
        Política y con el que se puede contactar por correo electrónico a{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        Si usted es residente de California, sujeto a las limitaciones y
        excepciones previstas por la ley, es posible que tenga a su disposición
        algunos derechos adicionales en virtud de la Ley de Protección al
        Consumidor de California (California Consumer Protection Act, CCPA) y su
        modificación por la Ley de Derechos de Privacidad de California. También
        puede solicitar acceso a las categorías y partes específicas de la
        información personal recabada sobre usted en los últimos doce meses
        (incluida la información personal divulgada con fines comerciales);
        saber si parte de su información personal se ha “vendido” o “compartido”
        y a quién; excluirse de cualquier “venta” o “intercambio” de su
        información personal si una empresa participa en la “venta” o
        “intercambio” de su información; y solicitar que una empresa corrija
        información personal inexacta. Tenga en cuenta que, aunque no vendemos
        su información personal, podemos compartirla con terceros con fines
        publicitarios utilizando “cookies”, como se describe en nuestra Política
        de Cookies.
      </p>
      <p>
        Según se describe anteriormente en la sección “Sus opciones”, si es uno
        de nuestros desarrolladores, puede acceder sin inconvenientes a la
        información de su cuenta en línea iniciando sesión en su cuenta o
        comunicándose con nosotros. Para ejercer de otro modo sus derechos de
        protección de datos, cuando corresponda, póngase en contacto con
        nosotros según se describe en la sección “Cómo ponerse en contacto con
        Plaid” a continuación. Es posible que deba proporcionar información
        adicional para confirmar su identidad antes de que podamos responder a
        su solicitud.
      </p>
      <p>
        Si recibimos su solicitud de un agente autorizado, podemos solicitar
        pruebas de que le ha proporcionado al agente una autorización válida por
        escrito para que envíe solicitudes en su nombre. Si usted es un agente
        autorizado que desea hacer una solicitud, póngase en contacto con
        nosotros según se describe en la sección “Cómo ponerse en contacto con
        Plaid” a continuación.
      </p>

      <h5 id='cambios-en-esta-declaración'>Cambios en esta declaración</h5>
      <p>
        Podemos actualizar o cambiar esta Declaración de privacidad
        periódicamente. Si realizamos alguna actualización o cambio, cambiaremos
        la fecha de entrada en vigor en la parte superior de esta Declaración.
        También podemos notificar cualquier cambio a través de otros medios,
        como publicar un aviso en nuestra página de inicio en{' '}
        <Link href='https://plaid.com'>
          <a>https://plaid.com</a>
        </Link>{' '}
        o enviarle un correo electrónico. Le animamos a que revise la
        Declaración de privacidad siempre que acceda a los servicios o
        interactúe de otro modo con nosotros para mantenerse informado sobre
        nuestras prácticas de datos y las opciones disponibles para usted.
      </p>

      <h5 id='cómo-ponerse-en-contacto-con-plaid-privacidad'>
        Cómo ponerse en contacto con Plaid
      </h5>
      <p>
        Si tiene cualquier pregunta relativa a esta Declaración de privacidad o,
        en general, a nuestras prácticas en materia de privacidad, póngase en
        contacto con nosotros a través del correo electrónico{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        o por correo postal:
      </p>
      <p>
        <em>Si reside fuera del EEE o del Reino Unido:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Inc. <br />
        A/A: Legal <br />
        PO Box 7775 #35278 <br />
        San Francisco, California 94120-7775, U.S.A.
      </div>
      <br />
      <p>
        Tanto para Plaid Financial Ltd. como para Plaid, B.V. hemos nombrado a
        un delegado de protección de datos (data protection officer, DPO) que
        también es responsable de supervisar las preguntas en relación con esta
        Política y con el que se puede contactar por correo electrónico a{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        <br />
        <em>Si reside en el Reino Unido:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Financial Ltd. <br />
        A/A: Legal <br />
        New Penderel House, 4th Floor <br />
        283-288 High Holborn <br />
        London, United Kingdom, WC1V 7HP
      </div>
      <p>
        <br />
        <em>Si reside en el EEE:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid, B.V. <br />
        A/A: Legal <br />
        Muiderstraat 1 <br />
        1011PZ Amsterdam <br />
        The Netherlands
      </div>

      <hr className='section-divider' />
    </>
  );
};

EsVersion.propTypes = propTypes;
export default EsVersion;
